import './styles/main.scss';
// @ts-ignore
import { getSessionManager } from '@finder/user-login/lib/session-manager';

import { applyAuthStateLoggedIn } from './core/auth-state';
import { EventBus } from './core/event-bus';
import { Registry } from './core/registry';
import { Session } from './core/session';
import { readWindowData } from './core/window-data';
import finderbotInit from './modules/finderbot';
import loginButtonsInit from './modules/login-buttons';
import navShellInit from './modules/nav-shell';
import searchInit from './modules/search';
import sideNavInit from './modules/side-nav';
import slideOutInit from './modules/slide-out';
import userMenuInit from './modules/user-menu';
import usefulnessFeedbackInit from './modules/usefulness-feedback';

declare global {
  interface Window {
    finderGlobalNav?: {
      getSessionManager: typeof getSessionManager;
      registry: Registry;
    };
    global_nav_host?: unknown;
  }
}

export function bootstrap(root: HTMLElement): Registry {
  const bus = new EventBus();
  const session = new Session();
  const state = readWindowData();
  const registry = new Registry(root, { root, bus, session, state });

  registry.register({
    name: 'nav-shell',
    trigger: { type: 'immediate' },
    inline: navShellInit,
  });

  registry.register({
    name: 'side-nav',
    trigger: { type: 'immediate' },
    selector: '#side-nav-ssr, .f1-sidenav-container',
    inline: sideNavInit,
  });

  registry.register({
    name: 'slide-out',
    trigger: { type: 'immediate' },
    selector: '[data-testid="slide-out-toggle"], [data-gn-trigger="slide-out"]',
    inline: slideOutInit,
  });

  registry.register({
    name: 'finderbot',
    trigger: { type: 'immediate' },
    selector: '[data-gn-trigger="finderbot"]',
    inline: finderbotInit,
  });

  registry.register({
    name: 'usefulness-feedback',
    trigger: { type: 'immediate' },
    selector: '[data-testid="slide-out-toggle"], [data-gn-trigger="slide-out"]',
    inline: usefulnessFeedbackInit,
  });

  registry.register({
    name: 'search',
    trigger: { type: 'immediate' },
    selector:
      '.search-entry-point-home, .categorySearchButton, [data-gn-trigger="search"], [data-gn-trigger="search-open"]',
    inline: searchInit,
  });

  registry.register({
    name: 'user-menu',
    trigger: { type: 'immediate' },
    selector: '[data-gn-trigger="user-menu"], [data-testid^="global-nav-user-avatar-button"]',
    inline: userMenuInit,
  });

  registry.register({
    name: 'login-buttons',
    trigger: { type: 'immediate' },
    selector:
      '[data-gn-trigger="login"], [data-gn-trigger="join"], [data-testid="global-nav-join-button-desktop"], [data-testid="global-nav-log-in-button-desktop"]',
    inline: loginButtonsInit,
  });

  registry.register({
    name: 'user-notifications',
    trigger: { type: 'immediate' },
    selector: '.notificationButton, [data-testid="notification-button"]',
    load: () => import('./modules/user-notifications'),
  });

  registry.register({
    name: 'active-rewards',
    trigger: { type: 'immediate' },
    selector: '[data-testid="active-rewards"]',
    load: () => import('./modules/active-rewards'),
  });

  registry.register({
    name: 'welcome-popup',
    trigger: {
      type: 'login',
    },
    load: () => import('./modules/welcome-popup'),
  });

  registry.register({
    name: 'trending-strap',
    trigger: { type: 'idle' },
    selector: '.trending-strap',
    load: () => import('./modules/trending-strap'),
  });

  registry.register({
    name: 'in-page-nav',
    trigger: { type: 'idle' },
    selector: '.in-page-navigation-navbar, .in-page-navigation-menu',
    load: () => import('./modules/in-page-nav'),
  });

  registry.start();

  window.finderGlobalNav = { getSessionManager, registry };

  let wasLoggedIn = session.isLoggedIn();
  session.onAuthChange(() => {
    const nowLoggedIn = session.isLoggedIn();
    if (!wasLoggedIn && nowLoggedIn) {
      applyAuthStateLoggedIn(registry);
    }
    wasLoggedIn = nowLoggedIn;
  });

  // Emulate webpack Module Federation Container API for v1 consumers:
  // get(name): Promise<Factory>, where factory(): Module is SYNC.
  // (Consumers do `factory()` not `await factory()`.)
  window.global_nav_host = {
    init: async () => undefined,
    get: (name: string) => {
      const mod =
        name === './sessionManager' ? { getSessionManager } : name === './app' ? {} : null;
      if (!mod) {
        return Promise.reject(new Error(`global_nav_host emulation: unknown export ${name}`));
      }
      return Promise.resolve(() => mod);
    },
    sharedDeps: undefined,
  };

  return registry;
}

const root = document.getElementById('header-sitewide') ?? document.getElementById('global-nav-v2');
if (root) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bootstrap(root), { once: true });
  } else {
    bootstrap(root);
  }
}
